<template>
  <Layout>
    <div class="mc-delivery">
      <div class="mc-list-header">
        <PageHeader :title="title" />
        <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
          <McButtonBack />
        </div>
      </div>
      <McTable
        v-if="!unauthorizedExternalRef"
        :key="`table-${tableKey}`"
        :fields="fields"
        :apiClient="apiClient"
        :hasSelectAll="false"
        :hasDelete="false"
        :hasCheckbox="false"
        :searchable="true"
        :activeFilters="activeFilters"
        stickyHeader
        withCard
        ref="mc-table-delivery-accurate"
        class="table-delivery-accurate"
        @listRes="listRes"
      >
        <template #before-table>
          <McTableFilter
            class="d-flex justify-content-end mb-3"
            :activeFilters="activeFilters"
            :parseFilterDataMappings="parseFilterDataMappings"
            @submit="handleChangeActiveFilters"
          >
            <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
              <InputTableFilter
                :formValues="formValues"
                :fullObjects="fullObjects"
                @update:formValues="updateFormValues($event)"
                @update:fullObjects="updateFullObjects($event)"
              />
            </template>
          </McTableFilter>
        </template>

        <template #cell(action)="data">
          <McButton
            variant="primary"
            :text="$t('delivery.provider.createDelivery')"
            icon="icon-fill_plus"
            @click="validateGeocode({ id: data.item.external_id })"
            :disabled="
              (data.item.status !== null && data.item.status?.name !== 'Dibatalkan') || storing
            "
          />
        </template>

        <template #cell(external_number)="{ value }">{{ value }}</template>
        <template #cell(delivery_order_numbers)="{ value }">
          <!-- Only show latest order number (last index) -->
          <span
            class="delivery-order-number"
            v-if="value.length > 0"
            @click="() => deliveryOrderNumberClick(value[value.length - 1].id)"
          >
            {{ value[value.length - 1].number }}
          </span>
        </template>
        <template #cell(status.name)="{ value, item }">
          <template>
            <span
              v-if="item.status?.color"
              class="__status-dots"
              :style="{ backgroundColor: item.status.color }"
            ></span>
            <span class="mc-text-status">{{ value }}</span>
          </template>
        </template>
        <template #cell(external_date)="{ value }">
          <div>{{ formatIndexDate(value).date }}</div>
        </template>
        <template #cell(integrated_on)="{ value }">
          <div v-if="value">
            <div>{{ formatIndexDate(value).date }}</div>
            <div class="__time">{{ formatIndexDate(value).time }}</div>
          </div>
        </template>
      </McTable>
      <b-card v-else>
        <McEmptyState
          :title="$t('delivery.provider.notConnectedWithAccurate')"
          :desc="$t('delivery.provider.pleaseConnect')"
          :buttonText="$t('global.goToSetting')"
          buttonIco=""
          @buttonPrimaryAction="$router.push({ name: 'Integration' })"
        />
      </b-card>
      <ModalAddress
        :value.sync="modalAddress"
        :address="address"
        @handleConfirm="handleConfirm($event)"
        :storing="storing"
        :key="`modal-${modalKey}`"
      />
    </div>
  </Layout>
</template>
<script>
import { mapGetters } from 'vuex'
import { MixPanel } from '@/mixin/mixpanel'
import McEmptyState from '@/components/McEmptyState'
import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import Layout from '@/router/layouts/main'
import McTable from '@/components/McTable'
import fields from './lib/fields/listAccurateDoFields'
import { formatIndexDate } from '@/helpers/tzDate'
import * as Delivery from '@/services/rest-api/lib/delivery/delivery-order'
import * as Geolocation from '@/services/rest-api/lib/delivery/geolocation'
import { errorMessage } from '@/helpers/errorHandler'
import errorMessageList from './lib/constants/provider/error-message-mapper'
import McButtonBack from '@/components/McButtonBack'
import ModalAddress from './components/Provider/ModalAddress'
import McTableFilter from '@/components/McTableFilter'
import InputTableFilter from './components/InputAccurateTableFilter'
import parseFilterDataMappings from './lib/constants/accurate-parse-filter-data-mappings'
import filterMappings from './lib/constants/accurate-filter-mappings'
import { Helper } from '@/mixin/helper'

export default {
  components: {
    McEmptyState,
    PageHeader,
    McButton,
    McTable,
    Layout,
    McButtonBack,
    McTableFilter,
    InputTableFilter,
    ModalAddress,
  },
  mixins: [MixPanel, fields],
  data() {
    return {
      params: {},
      tableKey: 1,
      metadata: null,
      storing: false,
      accurateDeliveriesTable: null,
      unauthorizedExternalRef: false,
      activeFilters: {},
      integrated: 'ShowAll',
      modalAddress: false,
      msg: this.$t('delivery.provider.invalidAddress'),
      address: {},
      externalId: null,
      successfulCreateDOPayload: null,
      currentAddress: null,
      modalKey: 1,
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    title() {
      return this.$t('delivery.title.dofromAccurate')
    },
    apiClient() {
      let getDeliveryOrder = Delivery.getDOAccurate(
        null,
        this.handleSetParams,
        this.handleSetMetadata
      )
      if (Object.keys(this.activeFilters).length > 0) {
        const queryParamFilters = {}

        Object.keys(this.activeFilters).forEach((key) => {
          let filterValue = this.activeFilters[key]

          if (key === 'external_date:lte') {
            return
          } else if (key === 'external_date:gte') {
            key = `external_date:between`
            filterValue = `${this.activeFilters['external_date:lte']},${filterValue}`
          } else if (key === 'integrated:is') {
            if (filterValue === 'Yes') {
              filterValue = true
            } else if (filterValue === 'No') {
              filterValue = false
            } else filterValue = null
          }

          const [newFilterKey, newFilterValue] =
            filterMappings[key]?.(key, filterValue) ?? undefined

          this.$set(queryParamFilters, newFilterKey, newFilterValue)
        })

        getDeliveryOrder = Delivery.getDOAccurate(
          queryParamFilters,
          this.handleSetParams,
          this.handleSetMetadata
        )
      }

      return {
        get: async (queryParam) => {
          const res = await getDeliveryOrder(queryParam)
          await this.handleAccurateErrorsV2(res?.errors_v2, {
            title: 'Gagal menarik DO/SO dari Accurate',
          })
          return res
        },
        delete: Delivery.deleteMany,
        storeExternal: Delivery.storeExternal,
        validateGeocode: Delivery.validateGeocode,
        storeAlias: Geolocation.storeAlias,
      }
    },
    parseFilterDataMappings() {
      return parseFilterDataMappings
    },
  },
  watch: {
    '$i18n.locale': function () {
      this.modalKey += 1
    },
  },
  mounted() {
    this.$nextTick(() => {
      let self = this
      self.accurateDeliveriesTable = self.$refs['mc-table-delivery-accurate'] // returns undefined ???
    })
  },
  methods: {
    formatIndexDate,
    handleSetParams(params) {
      this.params = params
    },
    handleSetMetadata(metadata) {
      this.metadata = metadata
    },
    async validateGeocode(payload) {
      this.externalId = payload.id
      try {
        this.storing = true
        const { origin, destination, errors, errors_v2 } = await this.apiClient.validateGeocode(
          payload,
          'ACCURATE'
        )

        if (errors) {
          this.handleCreateDOErrors(errors)
          return
        }

        const hasAccurateErrors = await this.handleAccurateErrorsV2(errors_v2, {
          title: 'Gagal memvalidasi alamat dari Accurate',
        })
        if (hasAccurateErrors) return

        this.address = { origin, destination }
        this.currentAddress = { origin: origin.address, destination: destination.address }
        this.modalAddress = true
      } catch (e) {
        console.error(e)
      } finally {
        this.storing = false
      }
    },
    async createDOMEP(payload) {
      this.externalId = payload.id
      try {
        this.storing = true
        this.$mcToast.warning('Mohon tunggu')
        const { id, errors, errors_v2 } = await this.apiClient.storeExternal(payload, 'ACCURATE')

        const hasErrors = errors && Object.keys(errors).length > 0
        if (hasErrors) {
          this.handleCreateDOErrors(errors)
          return
        }

        const hasAccurateErrors = await this.handleAccurateErrorsV2(errors_v2, {
          title: 'Gagal membuat DO MEP dari data Accurate',
        })
        if (hasAccurateErrors) {
          return
        }

        // Save alias asynchronously when create DO success
        this.saveDOAddressAlias(payload)

        this.track('Create Pengiriman dari Accurate')
        this.track('Create Pengiriman')

        this.accurateDeliveriesTable.getData({
          limit: this.accurateDeliveriesTable.limit,
          page: this.accurateDeliveriesTable.page,
          search:
            this.accurateDeliveriesTable.search != ''
              ? this.accurateDeliveriesTable.search
              : undefined,
          sorts: this.accurateDeliveriesTable.sorts.map((sort) => `${sort.key} ${sort.order}`),
        })

        const detailUrl = this.$router.resolve({
          name: 'Delivery Detail',
          params: { id },
          query: { isCreatedExternally: true },
        }).href
        window.open(detailUrl, '_blank')
      } catch (e) {
        console.error(e)
      } finally {
        this.storing = false
      }
    },
    async saveDOAddressAlias(createDOPayload) {
      if (createDOPayload.origin || createDOPayload.destination) {
        const payload = []
        if (createDOPayload.origin) {
          payload.push({
            alias: this.currentAddress.origin,
            latitude: createDOPayload.origin.latitude,
            longitude: createDOPayload.origin.longitude,
            province: createDOPayload.origin.province,
            city: createDOPayload.origin.city,
          })
        }
        if (createDOPayload.destination) {
          payload.push({
            alias: this.currentAddress.destination,
            latitude: createDOPayload.destination.latitude,
            longitude: createDOPayload.destination.longitude,
            province: createDOPayload.destination.province,
            city: createDOPayload.destination.city,
          })
        }

        await this.apiClient.storeAlias({ aliases: payload })
      }
    },
    async handleAccurateErrorsV2(errorsV2, options = {}) {
      if (!errorsV2) return false

      const issues = errorsV2.issues?.filter((item) => item.source === 'accurate') ?? []
      if (!issues.length) return false

      const { errorsMessageHtml, extractedUrl } =
        Helper.methods.formatAccurateErrorMessages(errorsV2.issues)

      await this.$mcMessageDialog.error({
        title: options.title || 'Gagal memproses data dari Accurate',
        subtitle: options.subtitle || 'Pesan dari Accurate:',
        content: errorsMessageHtml,
        useHtml: true,
        secondaryButton: { text: 'Salin Link Instalasi', visible: !!extractedUrl },
      }).then(async (action) => {
        if (action === 'secondary' && extractedUrl) {
          try {
            await navigator.clipboard.writeText(extractedUrl)
            this.$mcToast.success('Link instalasi berhasil disalin')
          } catch (copyErr) {
            console.error('Failed to copy:', copyErr)
            this.$mcToast.error('Gagal menyalin link instalasi')
          }
        }
      })

      return true
    },
    handleCreateDOErrors(errors) {
      let message = ''
      let refreshTable = false
      const location = ['origin', 'destination']
      const address = []
      for (let [key, val] of Object.entries(errors)) {
        if (key === 'origin' || key === 'destination') {
          if (location.includes(key)) {
            address.push(this.extractLocation(val[0]))
          }
        }

        if (errorMessageList[key]) {
          message = errorMessageList[key][val[0]]
          // If error is DO not found, refresh the table
          refreshTable = key === 'delivery_order' && val[0] === 'not found'
          break
        }
      }

      if (address.length) {
        this.address.origin = address[1]
        this.address.destination = address[0]
        return
      }
      this.$mcToast.error(errorMessage(message))

      if (refreshTable) {
        this.accurateDeliveriesTable.getData({
          limit: this.accurateDeliveriesTable.limit,
          page: this.accurateDeliveriesTable.page,
          search:
            this.accurateDeliveriesTable.search != ''
              ? this.accurateDeliveriesTable.search
              : undefined,
          sorts: this.accurateDeliveriesTable.sorts.map((sort) => `${sort.key} ${sort.order}`),
        })
      }
    },
    extractLocation(inputString) {
      const isFound = inputString.includes('found')
      const location = inputString.replace(/(found|not found)/i, '').trim()
      return isFound ? location : inputString
    },
    deliveryOrderNumberClick(deliveryOrderId) {
      const detailUrl = this.$router.resolve({
        name: 'Delivery Detail',
        params: { id: deliveryOrderId },
      }).href
      window.open(detailUrl, '_blank')
    },
    listRes(res) {
      const { errors, message } = res
      if (errors && message === 'unauthorized external reference') {
        this.unauthorizedExternalRef = true
      }
    },
    handleChangeActiveFilters(filters) {
      /*
        filters is Array of Object
        example:
        [
          {
            key: 'created_on:lte',
            values: '2023-08-29T17:00:00Z'
          }
        ]
        */

      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null or undefined
        if (filter.values === null || filter.values === undefined) return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)
      })
    },
    handleContinue() {
      this.modalAddress = true
    },
    handleConfirm(fullAddress) {
      /**
       * If province and city from map not found, fill the province and city from previous validate geocode
       * response. This occurs whenever geocode data found but user change the pinpoint only causing province
       * and city to be empty.
       */
      if (fullAddress.origin) {
        if (!fullAddress.origin.province)
          fullAddress.origin.province = this.address.origin.geocode.province
        if (!fullAddress.origin.city) fullAddress.origin.city = this.address.origin.geocode.city
      }
      if (fullAddress.destination) {
        if (!fullAddress.destination.province)
          fullAddress.destination.province = this.address.destination.geocode.province
        if (!fullAddress.destination.city)
          fullAddress.destination.city = this.address.destination.geocode.city
      }

      const payload = {
        id: this.externalId,
        ...fullAddress,
      }
      this.createDOMEP(payload)
      this.modalAddress = false
    },
  },
}
</script>
<style lang="scss" scoped>
:deep(.action-center) {
  display: flex;
  justify-content: center;
}

.table-delivery-accurate {
  .__status-dots {
    display: inline-block;
    margin: auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 8px;
  }

  .__time {
    color: $mep-color-text-200;
  }

  .delivery-order-number {
    color: $mep-primary;
    font-weight: 600;
    cursor: pointer;
  }
}
</style>
